// Protección contra doble autorización en los flujos de pago (OneClick /
// Webpay / pagar con descuento). Módulo puro (sin React/Zustand/Sentry),
// mismo patrón y misma regla "prohibido mockear en los tests de los
// componentes que lo consumen" que src/lib/oneclickCards.js,
// src/lib/mpCheckout.js y src/lib/mpReturn.js.
//
// Por qué un singleton de módulo (y no un useRef por componente):
// `/pay-detail` puede remontarse (navegación, F5) durante la ventana en la
// que un pago sigue en vuelo; un useRef se reinicia con el componente y
// dejaría de proteger justo en ese caso (equivalente directo del comentario
// de checkoutReturnGuard.js en Sima-appmovil).
//
// Por qué el candado en vuelo NO se keyea por `buyOrder`: el `buyOrder` pasa
// a regenerarse por intento (ver generarBuyOrder*), así que dos clics
// rápidos ya tendrían `buyOrder` distintos incluso antes de que el primero
// resuelva — un candado keyeado por `buyOrder` nunca bloquearía el segundo
// clic. Se usa una clave de flujo fija ("oneclick" | "webpay" |
// "paywithdiscount").

// Palanca de emergencia (mismo criterio que ONECLICK_FILTRO_ACTIVO /
// MP_TOTAL_TOLERANCIA = Infinity): en `false`, adquirirCandadoPago y
// reclamarIntento siempre devuelven `true` (comportamiento previo al fix),
// sin revertir código.
export const PAYMENT_LOCK_ACTIVO = true;

export const PAYMENT_MSG_EN_CURSO =
  "Estamos procesando tu pago, espera un momento";

// TTL de liberación automática del candado EN VUELO (memoria de módulo),
// análogo en espíritu a MP_VERIFY_TIMEOUT_MS (src/lib/mpReturn.js) para el
// timeout client-side de verificación de Mercado Pago. Es sólo una red de
// seguridad para el caso "el mutateAsync nunca resolvió" (timeout de red,
// usuario offline): sin esto, un candado tomado antes de un await que nunca
// resuelve queda tomado para siempre (el módulo es un singleton de proceso
// que sobrevive a la navegación SPA), bloqueando cualquier reintento
// legítimo posterior en la misma pestaña hasta un full reload. 30s es
// bastante mayor que un timeout de red normal (para no liberar un candado
// que sigue genuinamente en curso) pero no tan largo como para frustrar a un
// usuario que sólo quiere reintentar tras una caída de red real.
export const PAYMENT_LOCK_TTL_MS = 30 * 1000;

// TTL de limpieza de claims persistidos (sessionStorage). Holgado a
// propósito: cubre el ida-y-vuelta completo a Webpay, que ya tiene su propio
// contador de 240s para el refresh del token.
export const CHECKOUT_CLAIM_TTL_MS = 15 * 60 * 1000;

// Clave nueva y aditiva de sessionStorage; nunca reemplaza ni lee
// mp_buyOrder/mp_storeId/mp_checkout_snapshot.
export const CHECKOUT_CLAIM_KEY = "checkout_claims";

// ---------------------------------------------------------------------------
// Candado en vuelo (memoria de módulo, por flujo)
// ---------------------------------------------------------------------------

const candadosEnVuelo = new Map(); // flujo -> { adquiridoEn, token }
const bloqueosPorFlujo = new Map();
const yaReportadoPorFlujo = new Set();

// Contador incremental para generar tokens de adquisición únicos (ver
// adquirirCandadoPago/liberarCandadoPago). No necesita ser criptográfico:
// sólo tiene que distinguir "dueño actual" de "dueño obsoleto" dentro de la
// misma pestaña/proceso.
let contadorTokens = 0;
const generarTokenCandado = () =>
  `${Date.now()}-${++contadorTokens}-${Math.random().toString(36).slice(2)}`;

/**
 * expirarSiCorresponde(flujo)
 * Libera el candado de `flujo` si lleva más de PAYMENT_LOCK_TTL_MS tomado
 * (red de seguridad ante un mutateAsync que nunca resolvió). No hace nada
 * si el candado no existe o sigue vigente.
 */
const expirarSiCorresponde = (flujo) => {
  const entry = candadosEnVuelo.get(flujo);
  if (entry === undefined) return;

  if (Date.now() - entry.adquiridoEn > PAYMENT_LOCK_TTL_MS) {
    candadosEnVuelo.delete(flujo);
    bloqueosPorFlujo.delete(flujo);
    yaReportadoPorFlujo.delete(flujo);
  }
};

/**
 * adquirirCandadoPago(flujo)
 * Devuelve un token de adquisición (string único) sólo si no había un pago
 * en curso para ese flujo (y lo marca como en curso bajo ese token); `false`
 * si ya había un candado vigente tomado por otro intento. `flujo` falsy ->
 * siempre `true` (no se puede deduplicar sin clave, no bloquear). Antes de
 * decidir, libera automáticamente un candado expirado (ver
 * PAYMENT_LOCK_TTL_MS) para que un pago genuinamente colgado no bloquee
 * reintentos legítimos para siempre.
 *
 * El token existe para dar "ownership" al candado: sin él, la respuesta
 * tardía de un intento cuyo candado ya expiró por TTL podría liberar el
 * candado de un intento más nuevo que lo re-adquirió, dejando la puerta
 * abierta a un tercer clic superpuesto (ver liberarCandadoPago).
 */
export const adquirirCandadoPago = (flujo) => {
  if (!flujo) return true;
  if (!PAYMENT_LOCK_ACTIVO) return true;

  expirarSiCorresponde(flujo);

  if (candadosEnVuelo.has(flujo)) {
    const previo = bloqueosPorFlujo.get(flujo) ?? 0;
    bloqueosPorFlujo.set(flujo, previo + 1);
    return false;
  }

  const token = generarTokenCandado();
  candadosEnVuelo.set(flujo, { adquiridoEn: Date.now(), token });
  return token;
};

/**
 * esDuenioVigente(flujo, token)
 * Helper interno compartido: `true` sólo si `token` coincide exactamente con
 * el token actualmente registrado como dueño del candado en vuelo de
 * `flujo`. `flujo` falsy, o un `flujo` sin candado vigente, siempre `false`.
 */
const esDuenioVigente = (flujo, token) => {
  if (!flujo) return false;
  const actual = candadosEnVuelo.get(flujo);
  if (actual === undefined) return false;
  return actual.token === token;
};

/**
 * liberarCandadoPago(flujo, token)
 * Libera el candado en vuelo y reinicia el contador de bloqueos/reporte de
 * ese flujo, para que el próximo intento (legítimo) empiece limpio — pero
 * SÓLO si `token` coincide con el token actualmente registrado como dueño
 * del candado. Si no coincide (el candado ya expiró por TTL y fue
 * re-adquirido por un intento más nuevo, o ya fue liberado y reusado), la
 * llamada es un no-op silencioso: es simplemente una liberación tardía de un
 * dueño que ya no es el actual, y no debe poder afectar al candado vigente.
 */
export const liberarCandadoPago = (flujo, token) => {
  if (!flujo) return;
  if (!esDuenioVigente(flujo, token)) return;

  candadosEnVuelo.delete(flujo);
  bloqueosPorFlujo.delete(flujo);
  yaReportadoPorFlujo.delete(flujo);
};

/**
 * esTokenVigente(flujo, token)
 * Segunda capa de protección de ownership, más allá del candado en sí:
 * `true` sólo si `token` sigue siendo el dueño vigente del candado de
 * `flujo` en este preciso instante. Pensado para que la CADENA DE ÉXITO que
 * sigue corriendo después de que una petición de autorización/confirmación
 * resuelve (sendCreateTransaccion/sendPedido/createOrder) pueda verificar,
 * justo al recibir la respuesta y antes de cualquier efecto secundario
 * adicional, que no es la respuesta tardía de un intento obsoleto cuyo
 * candado ya expiró por TTL y fue re-adquirido por un intento más nuevo — a
 * diferencia de liberarCandadoPago, esta función NO libera ni muta nada, así
 * que puede llamarse cuantas veces sea necesario en el camino de éxito sin
 * efectos colaterales. `flujo` falsy -> siempre `false` (no hay candado del
 * cual ser dueño).
 */
export const esTokenVigente = (flujo, token) => esDuenioVigente(flujo, token);

export const hayPagoEnCurso = (flujo) => {
  if (!flujo) return false;
  expirarSiCorresponde(flujo);
  return candadosEnVuelo.has(flujo);
};

/**
 * resetCandadosPago()
 * Sólo para tests: limpia todo el estado de módulo entre casos.
 */
export const resetCandadosPago = () => {
  candadosEnVuelo.clear();
  bloqueosPorFlujo.clear();
  yaReportadoPorFlujo.clear();
};

/**
 * debeReportarBloqueo(flujo)
 * `true` sólo la primera vez que se bloquea un intento dentro del candado
 * vigente (evita ruido de Sentry por un usuario impaciente haciendo varios
 * clics mientras el primer intento sigue en vuelo).
 */
export const debeReportarBloqueo = (flujo) => {
  if (!flujo) return true;
  if (yaReportadoPorFlujo.has(flujo)) return false;
  yaReportadoPorFlujo.add(flujo);
  return true;
};

export const bloqueosAcumulados = (flujo) => {
  if (!flujo) return 0;
  return bloqueosPorFlujo.get(flujo) ?? 0;
};

// ---------------------------------------------------------------------------
// Claim de idempotencia persistido (sessionStorage)
// ---------------------------------------------------------------------------

const leerClaims = () => {
  try {
    const raw = sessionStorage.getItem(CHECKOUT_CLAIM_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    return parsed;
  } catch {
    return {};
  }
};

const escribirClaims = (claims) => {
  try {
    sessionStorage.setItem(CHECKOUT_CLAIM_KEY, JSON.stringify(claims));
  } catch {
    // sessionStorage no disponible/lleno: el claim degrada a "permitir";
    // el candado en memoria sigue cubriendo el caso intra-página.
  }
};

const limpiarExpirados = (claims, ttlMs) => {
  const ahora = Date.now();
  const resultado = {};
  for (const [key, entry] of Object.entries(claims)) {
    if (!entry || typeof entry.claimedAt !== "number") continue;

    const edad = ahora - entry.claimedAt;
    if (edad < 0) {
      // Reloj del sistema movido hacia atrás: no se considera expirado ni
      // se libera al instante; se reescribe claimedAt para que el TTL
      // corra desde ahora.
      resultado[key] = { claimedAt: ahora };
      continue;
    }
    if (edad > ttlMs) continue; // expirado: se descarta

    resultado[key] = entry;
  }
  return resultado;
};

/**
 * reclamarIntento(key, ttlMs)
 * `true` la primera vez por clave (y la reclama); `false` en llamadas
 * posteriores mientras el claim esté vigente. `key` vacía/null -> siempre
 * `true` (no bloquear sin poder deduplicar). Todo el acceso a sessionStorage
 * está en try/catch y nunca lanza — un storage roto degrada a "permitir".
 */
export const reclamarIntento = (key, ttlMs = CHECKOUT_CLAIM_TTL_MS) => {
  if (!key) return true;
  if (!PAYMENT_LOCK_ACTIVO) return true;

  try {
    const claims = limpiarExpirados(leerClaims(), ttlMs);

    if (claims[key]) {
      escribirClaims(claims);
      return false;
    }

    claims[key] = { claimedAt: Date.now() };
    escribirClaims(claims);
    return true;
  } catch {
    return true;
  }
};

/**
 * liberarIntento(key)
 * Libera explícitamente un claim (uso puntual: no se usa por defecto en el
 * camino de fallo de /pay-detail — ver comentario en OneClick.jsx — porque
 * liberarlo reabriría el re-cobro por F5; existe para los casos que sí
 * necesitan reintentar el mismo montaje, p. ej. tests).
 */
export const liberarIntento = (key) => {
  if (!key) return;
  try {
    const claims = leerClaims();
    delete claims[key];
    escribirClaims(claims);
  } catch {
    // no-op
  }
};

// ---------------------------------------------------------------------------
// Generadores de buyOrder por intento
// ---------------------------------------------------------------------------

// Prefijos obligatorios (createOrder.post.ts exige "Mall-"/"WP-" para emitir
// boleta) y longitud <= 20 chars (columna varchar(20) en BD).
export const generarBuyOrderOneClick = () =>
  "Mall-" + Math.round(Math.random() * 999999999);

export const generarBuyOrderWebpay = () =>
  "WP-" + Math.round(Math.random() * 999999999);
